import loadingGif from "@assets/BHPreloader.gif";
import { getErrorMessage } from "@helper/getErrorMessage";
import { isArrayWithElements, isClientSide } from "@helper/utils";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import useIntersectionObserver from "@hooks/useIntersectionObserver";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

interface LoadMoreProps {
  rootClassName?: string;
  onLoadMore: () => Promise<ProcessedArticleData[] | number>;
  virtualPVTitle: string;
  pageNumber: number;
  hasMore?: boolean;
  loadText?: string;
  isArticlePage?: boolean;
  trackVirtualPV?: boolean;
  autoLoadOnMobile?: boolean;
  showLoadingGif?: boolean;
  showButton?: boolean;
  buttonClass?: string;
}

/**
 * LoadMore component that triggers the onLoadMore function when the user clicks the load more button
 * or when the user scrolls to the bottom of the container.
 *
 * @param rootClassName - Optional class name for styling the root container.
 * @param onLoadMore - The function to load more content.
 * @param hasMore - A boolean indicating if there are more items to load.
 * @param loadText - The text to display on the load more button.
 * @returns A React element that displays the load more button or a loading gif.
 */
export default function LoadMore({
  rootClassName = "",
  onLoadMore,
  virtualPVTitle,
  pageNumber,
  hasMore = true,
  loadText = "More Stories",
  isArticlePage,
  trackVirtualPV = true,
  autoLoadOnMobile = false,
  showLoadingGif = false,
  showButton = false,
  buttonClass = "",
}: LoadMoreProps): React.ReactElement {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(showLoadingGif);
  const [error, setError] = useState<string>("");
  const analyticsCall = useBHVirtualPVAnalyticsCall();

  const handleLoadMore = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await onLoadMore();
      if (
        trackVirtualPV &&
        (isArrayWithElements(response) || response > 0) &&
        typeof window !== "undefined"
      ) {
        let title = virtualPVTitle;
        if (isArticlePage && isArrayWithElements(response)) {
          title = response[0].title;
        }
        analyticsCall(title, undefined, undefined, undefined, pageNumber + 1);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [
    onLoadMore,
    virtualPVTitle,
    isArticlePage,
    analyticsCall,
    pageNumber,
    trackVirtualPV,
  ]);

  const handleVisibilityChange = useCallback(
    (element: Element) => {
      if (element === loadMoreRef.current && hasMore) {
        handleLoadMore().catch((err) => {
          console.error(getErrorMessage(err));
        });
      }
    },
    [handleLoadMore, hasMore],
  );

  const { observe, unobserve } = useIntersectionObserver(
    handleVisibilityChange,
    {
      root: null,
      rootMargin: "0px",
      threshold: 1,
    },
  );

  useEffect(() => {
    if (!isClientSide) {
      return;
    }

    const element = loadMoreRef.current;
    const onScroll = () => {
      document.removeEventListener("scroll", onScroll);
      if (element) {
        observe(element);
      }
    };

    const shouldAutoLoad = showButton
      ? false
      : window.innerWidth >= 768 || autoLoadOnMobile;

    if (shouldAutoLoad) {
      document.addEventListener("scroll", onScroll);
    }

    return () => {
      if (shouldAutoLoad) {
        document.removeEventListener("scroll", onScroll);
      }
      if (element) {
        unobserve(element);
      }
    };
  }, [observe, unobserve, autoLoadOnMobile, showButton]);

  const renderButtonText = () => {
    if (error) {
      return error;
    }

    return loadText;
  };

  return (
    <>
      {isLoading ? (
        <LoadingGif />
      ) : (
        <div ref={loadMoreRef} className="col-span-full">
          <div
            className={cx(
              "cursor-pointer border-y-[2px] border-blue-300 py-md text-center",
              {
                hidden: !hasMore || autoLoadOnMobile,
              },
              !showButton && "md:hidden",
              rootClassName,
            )}
            onClickCapture={() => {
              handleLoadMore().catch((err) => {
                console.error(getErrorMessage(err));
              });
            }}
          >
            <button className={twMerge("uppercase outline-none", buttonClass)}>
              {renderButtonText()}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function LoadingGif() {
  return (
    <div
      data-testid="loading-gif"
      className="flex col-span-full justify-center"
    >
      <img src={loadingGif} alt="Loading..." className="rounded" />
    </div>
  );
}
